/**
 * Évolution hebdomadaire de la difficulté moyenne jouée.
 *
 * C'est l'indicateur de progression le plus direct qu'on puisse tirer du
 * tracker : si les utilisateurs montent en niveau, la courbe monte.
 *
 * ⚠️ Elle mesure une moyenne de POPULATION, pas une progression individuelle.
 * Un afflux de débutants la fait baisser sans que personne n'ait régressé.
 * L'analyse par utilisateur (S1 vs S4) répondra à cette question-là.
 */

import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatDayLong, formatDayShort, formatDecimal, formatInteger } from '../lib/format';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn, WeeklyDifficultyPoint } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<WeeklyDifficultyPoint>> = [
  { key: 'week', header: 'Semaine du', render: (row) => formatDayLong(row.week) },
  {
    key: 'avg',
    header: 'Difficulté moyenne',
    numeric: true,
    render: (row) => formatDecimal(row.avgDifficulty),
  },
  { key: 'learns', header: 'Apprentissages', numeric: true, render: (row) => formatInteger(row.learns) },
];

export function WeeklyDifficultyChart({ data }: { data: readonly WeeklyDifficultyPoint[] }) {
  return (
    <ChartCard
      title="Difficulté moyenne par semaine"
      subtitle="Moyenne de population — un afflux de débutants la fait baisser"
      rows={data}
      columns={columns}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data as WeeklyDifficultyPoint[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="week"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            // Echelle fixe sur l'etendue reelle de la difficulte : laisser
            // l'axe s'auto-ajuster transformerait une variation de 0,05 en
            // montagne russe.
            domain={[1, 5]}
            ticks={[1, 2, 3, 4, 5]}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={36}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip labelFormatter={formatDayLong} valueFormatter={(v) => formatDecimal(v)} />
            }
          />
          <Line
            isAnimationActive={false}
            type="monotone"
            dataKey="avgDifficulty"
            name="Difficulté moyenne"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            dot={false}
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
